export const BOOKING_FLOW_TRAFFIC_CLASSES = [
  'internal',
  'external',
  'unknown',
] as const;

export type BookingFlowTrafficClass = typeof BOOKING_FLOW_TRAFFIC_CLASSES[number];

export const INTERNAL_BOOKING_FLOW_ID_PREFIX = 'internal_';

const INTERNAL_QUERY_KEYS = [
  'ml_internal',
  'mintleaf_internal',
  'internalTraffic',
] as const;

const INTERNAL_QUERY_VALUES = new Set(['1', 'true', 'yes']);

const isMintleafHostname = (hostname: string): boolean => {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, '');
  return normalized === 'mintleaf.hu' || normalized.endsWith('.mintleaf.hu');
};

const hasExplicitInternalMarker = (url: URL): boolean =>
  INTERNAL_QUERY_KEYS.some(key => {
    const value = url.searchParams.get(key)?.trim().toLowerCase();
    return value !== undefined && value !== null && INTERNAL_QUERY_VALUES.has(value);
  });

export const classifyBookingFlowTraffic = (input: {
  pageUrl?: string | null;
  referrer?: string | null;
}): BookingFlowTrafficClass => {
  try {
    if (input.pageUrl) {
      const pageUrl = new URL(input.pageUrl);
      if (hasExplicitInternalMarker(pageUrl)) return 'internal';
    }
  } catch {
    // Invalid or unavailable URL metadata must never hide guest traffic.
  }

  try {
    if (input.referrer) {
      const referrer = new URL(input.referrer);
      if (isMintleafHostname(referrer.hostname)) return 'internal';
      return 'external';
    }
  } catch {
    // Keep uncertain legacy/browser cases visible by returning unknown.
  }

  return 'unknown';
};

export const getBrowserBookingFlowTrafficClass = (): BookingFlowTrafficClass => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 'unknown';
  return classifyBookingFlowTraffic({
    pageUrl: window.location.href,
    referrer: document.referrer,
  });
};

export const isInternalBookingFlowId = (bookingFlowId: string): boolean =>
  bookingFlowId.startsWith(INTERNAL_BOOKING_FLOW_ID_PREFIX);

export const createTrafficScopedBookingFlowId = (
  bookingFlowId: string,
  trafficClass: BookingFlowTrafficClass,
): string => trafficClass === 'internal'
  ? `${INTERNAL_BOOKING_FLOW_ID_PREFIX}${bookingFlowId}`.slice(0, 128)
  : bookingFlowId;
